import React from 'react';
import { GasBadge, GasEmptyState, GasTabPanel, GasTabs, useGasI18n } from '../../shared/kit';
import type { GasTab } from '../../shared/kit';
import type {
    ShopCatalogItem,
    ShopCatalogSortBy,
    ShopCatalogSortDir,
    ShopCatalogTab,
    ShopProductCatalogInitData,
} from './types';

interface AppProps {
    initData: ShopProductCatalogInitData;
}

function clampColumns(columns: number | undefined): number {
    const value = Number(columns || 3);
    return Math.max(1, Math.min(4, value));
}

function clampItemsPerPage(itemsPerPage: number | undefined): number {
    const value = Number(itemsPerPage || 12);
    return Math.max(6, value);
}

function toSearchHaystack(item: ShopCatalogItem): string {
    return `${item.title} ${item.description || ''}`.toLowerCase();
}

function truncateDescription(value: string, maxLength = 140): string {
    if (!value || value.length <= maxLength) {
        return value;
    }

    return value.slice(0, maxLength) + '…';
}

function sortItems(items: ShopCatalogItem[], sortBy: ShopCatalogSortBy, sortDir: ShopCatalogSortDir): ShopCatalogItem[] {
    const direction = sortDir === 'asc' ? 1 : -1;

    return [...items].sort(function (a, b) {
        if (sortBy === 'featured') {
            const featuredDelta = Number(Boolean(b.is_featured)) - Number(Boolean(a.is_featured));
            if (featuredDelta !== 0) {
                return featuredDelta * direction;
            }

            const rankDelta = Number(a.featured_rank ?? 100) - Number(b.featured_rank ?? 100);
            if (rankDelta !== 0) {
                return rankDelta * direction;
            }

            return String(a.title || '').localeCompare(String(b.title || ''), 'fr-CH', { sensitivity: 'base' });
        }

        if (sortBy === 'price') {
            return (Number(a.price || 0) - Number(b.price || 0)) * direction;
        }

        if (sortBy === 'type') {
            return String(a.type || '').localeCompare(String(b.type || ''), 'fr-CH', { sensitivity: 'base' }) * direction;
        }

        return String(a.title || '').localeCompare(String(b.title || ''), 'fr-CH', { sensitivity: 'base' }) * direction;
    });
}

export default function App({ initData }: AppProps) {
    const { t, tn } = useGasI18n();

    const items = React.useMemo<ShopCatalogItem[]>(function () {
        return Array.isArray(initData.items) ? initData.items : [];
    }, [initData.items]);

    const columns = React.useMemo(() => clampColumns(initData.columns), [initData.columns]);
    const itemsPerPage = React.useMemo(() => clampItemsPerPage(initData.itemsPerPage), [initData.itemsPerPage]);
    const typeLabels = React.useMemo<Record<string, string>>(() => initData.typeLabels || {}, [initData.typeLabels]);

    const [activeTab, setActiveTab] = React.useState<ShopCatalogTab>('cards');
    const [search, setSearch] = React.useState('');
    const [selectedTypes, setSelectedTypes] = React.useState<Set<string>>(new Set());
    const [selectedCategoryId, setSelectedCategoryId] = React.useState<number | null>(null);
    const [currentPage, setCurrentPage] = React.useState(1);

    const [sortBy, setSortBy] = React.useState<ShopCatalogSortBy>('featured');
    const [sortDir, setSortDir] = React.useState<ShopCatalogSortDir>('asc');

    const categories = React.useMemo(
        () => (Array.isArray(initData.categories) ? initData.categories : []),
        [initData.categories],
    );

    const allTypes = React.useMemo(() => {
        const set = new Set<string>();
        items.forEach(function (item) {
            set.add(String(item.type || ''));
        });

        return Array.from(set);
    }, [items]);

    const filteredItems = React.useMemo(function () {
        const q = search.trim().toLowerCase();

        return items.filter(function (item) {
            if (selectedTypes.size > 0 && !selectedTypes.has(String(item.type || ''))) {
                return false;
            }

            if (selectedCategoryId !== null) {
                if (Number(item.primary_category_id || 0) !== selectedCategoryId) {
                    return false;
                }
            }

            if (q !== '' && !toSearchHaystack(item).includes(q)) {
                return false;
            }

            return true;
        });
    }, [items, search, selectedTypes, selectedCategoryId]);

    const visibleTypes = React.useMemo(function () {
        const set = new Set<string>();
        filteredItems.forEach(function (item) {
            set.add(String(item.type || ''));
        });

        return Array.from(set);
    }, [filteredItems]);

    const isMembershipPlanOnly = React.useMemo(function () {
        return visibleTypes.length === 1 && visibleTypes[0] === 'membership_plan';
    }, [visibleTypes]);

    const sortedLineItems = React.useMemo(function () {
        return sortItems(filteredItems, sortBy, sortDir);
    }, [filteredItems, sortBy, sortDir]);

    const activeItems = activeTab === 'lines' ? sortedLineItems : filteredItems;

    const totalPages = React.useMemo(function () {
        return Math.max(1, Math.ceil(activeItems.length / itemsPerPage));
    }, [activeItems.length, itemsPerPage]);

    React.useEffect(
        function () {
            if (currentPage > totalPages) {
                setCurrentPage(totalPages);
            }
        },
        [currentPage, totalPages],
    );

    const paginatedItems = React.useMemo(function () {
        const start = (currentPage - 1) * itemsPerPage;
        return activeItems.slice(start, start + itemsPerPage);
    }, [activeItems, currentPage, itemsPerPage]);

    const hasFilters = search.trim() !== '' || selectedTypes.size > 0 || selectedCategoryId !== null;
    const showEmpty = activeItems.length === 0;

    const tabs = React.useMemo<GasTab[]>(
        function () {
            return [
                { value: 'cards', label: t('shop.catalog.tabs.cards') },
                { value: 'lines', label: t('shop.catalog.tabs.lines') },
            ];
        },
        [t],
    );

    function toggleType(type: string): void {
        setSelectedTypes(function (current) {
            const next = new Set(current);

            if (next.has(type)) {
                next.delete(type);
            } else {
                next.add(type);
            }

            return next;
        });

        setCurrentPage(1);
    }

    function clearFilters(): void {
        setSearch('');
        setSelectedTypes(new Set());
        setSelectedCategoryId(null);
        setCurrentPage(1);
    }

    function goToPage(page: number): void {
        const nextPage = Math.max(1, Math.min(totalPages, Number(page) || 1));
        setCurrentPage(nextPage);

        if (typeof document !== 'undefined') {
            const root = document.getElementById('gas-shop-product-catalog-app');
            if (root && typeof root.scrollIntoView === 'function') {
                root.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    function formatPrice(amount: number): string {
        const n = Number.parseFloat(String(amount));

        if (Number.isNaN(n) || n <= 0) {
            return t('common.free');
        }

        return new Intl.NumberFormat('fr-CH', {
            style: 'currency',
            currency: 'CHF',
            minimumFractionDigits: 2,
        }).format(n);
    }

    function typeBadgeTone(type: string): 'default' | 'success' | 'warning' | 'danger' | 'muted' {
        const map: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'muted'> = {
            activite: 'default',
            membership_plan: 'warning',
            cotisation: 'default',
            produit_digital: 'success',
            produit_physique: 'muted',
        };

        return map[type] || 'default';
    }

    function typeLabel(type: string): string {
        return typeLabels[type] || type;
    }

    function onSortChange(event: React.ChangeEvent<HTMLSelectElement>): void {
        setSortBy(event.target.value as ShopCatalogSortBy);
        setCurrentPage(1);
    }

    function toggleSortDirection(): void {
        setSortDir(function (current) {
            return current === 'asc' ? 'desc' : 'asc';
        });
        setCurrentPage(1);
    }

    return (
        <div className="gas-shop-catalog-react">
            <div className="gas-shop-catalog-react__toolbar">
                <div className="gas-shop-catalog-react__search">
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder={t('shop.catalog.search_placeholder')}
                        className="gas-shop-catalog-react__search-input"
                        aria-label={t('shop.catalog.search_placeholder')}
                    />
                </div>

                {allTypes.length > 1 ? (
                    <div className="gas-shop-catalog-react__type-filters">
                        {allTypes.map(function (type) {
                            return (
                                <button
                                    key={type}
                                    type="button"
                                    className={[
                                        'gas-shop-catalog-react__type-pill',
                                        selectedTypes.has(type) ? 'is-active' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={() => toggleType(type)}
                                >
                                    {typeLabel(type)}
                                </button>
                            );
                        })}
                        {hasFilters ? (
                            <button
                                type="button"
                                className="gas-shop-catalog-react__clear"
                                onClick={clearFilters}
                            >
                                × {t('shop.catalog.clear_filters')}
                            </button>
                        ) : null}
                    </div>
                ) : null}

                {categories.length > 0 ? (
                    <div className="gas-shop-catalog-react__type-filters" role="group" aria-label={t('shop.catalog.filter_category')}>
                        <button
                            type="button"
                            className={[
                                'gas-shop-catalog-react__type-pill',
                                selectedCategoryId === null ? 'is-active' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onClick={() => {
                                setSelectedCategoryId(null);
                                setCurrentPage(1);
                            }}
                        >
                            {t('shop.catalog.category_all')}
                        </button>
                        {categories.map(function (cat) {
                            const id = Number(cat.id);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    className={[
                                        'gas-shop-catalog-react__type-pill',
                                        selectedCategoryId === id ? 'is-active' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={() => {
                                        setSelectedCategoryId(id);
                                        setCurrentPage(1);
                                    }}
                                >
                                    {cat.label || cat.slug}
                                </button>
                            );
                        })}
                    </div>
                ) : null}
            </div>

            {filteredItems.length > 0 ? (
                <p className="gas-shop-catalog-react__count">
                    <strong>{filteredItems.length}</strong> {tn('shop.catalog.products_count', filteredItems.length)}
                    {hasFilters ? (
                        <>
                            {' · '}
                            <button
                                type="button"
                                className="gas-shop-catalog-react__see-all"
                                onClick={clearFilters}
                            >
                                {t('shop.catalog.see_all')}
                            </button>
                        </>
                    ) : null}
                </p>
            ) : null}

            <GasTabs
                tabs={tabs}
                value={activeTab}
                onValueChange={(value) => {
                    setActiveTab(value as ShopCatalogTab);
                    setCurrentPage(1);
                }}
                ariaLabel={t('shop.catalog.tabs.aria')}
                className="gas-shop-catalog-react__tabs"
            >
                <GasTabPanel value="cards">
                    {!showEmpty ? (
                        <div
                            className={[
                                'gas-shop-catalog-react__grid',
                                `gas-shop-catalog-react__grid--cols-${columns}`,
                                isMembershipPlanOnly ? 'gas-shop-catalog-react__grid--membership-plan-only' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            {paginatedItems.map(function (item) {
                                return (
                                    <article
                                        key={item.id}
                                        className={`gas-shop-catalog-react__card gas-shop-catalog-react__card--${item.type}`}
                                    >
                                        <a className="gas-shop-catalog-react__card-link" href={item.url}>
                                            <div className="gas-shop-catalog-react__card-media">
                                                {item.image_url ? (
                                                    <img
                                                        src={item.image_url}
                                                        alt={item.title}
                                                        className="gas-shop-catalog-react__card-image"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <span
                                                        className="gas-shop-catalog-react__card-image-placeholder"
                                                        aria-hidden="true"
                                                    >
                                                        {(item.title || 'P').charAt(0).toUpperCase()}
                                                    </span>
                                                )}
                                            </div>

                                            <GasBadge tone={typeBadgeTone(String(item.type || ''))}>
                                                {typeLabel(String(item.type || ''))}
                                            </GasBadge>
                                            {item.is_featured ? (
                                                <GasBadge tone="warning">
                                                    {t('shop.catalog.featured_badge')}
                                                </GasBadge>
                                            ) : null}

                                            <h2 className="gas-shop-catalog-react__card-title">{item.title}</h2>

                                            {item.description ? (
                                                <p className="gas-shop-catalog-react__card-description">
                                                    {truncateDescription(item.description)}
                                                </p>
                                            ) : null}

                                            <div className="gas-shop-catalog-react__card-footer">
                                                <span className="gas-shop-catalog-react__card-price">
                                                    {formatPrice(item.price)}
                                                </span>
                                                <span className="gas-shop-catalog-react__card-cta">
                                                    {t('shop.catalog.view_product')}
                                                </span>
                                            </div>
                                        </a>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (
                        <GasEmptyState
                            title={
                                hasFilters
                                    ? t('shop.catalog.empty_filtered_title')
                                    : t('shop.catalog.empty_all_title')
                            }
                            message={
                                hasFilters
                                    ? t('shop.catalog.empty_filtered_description')
                                    : ''
                            }
                            actions={
                                hasFilters ? (
                                    <button
                                        type="button"
                                        className="gas-button gas-button--primary"
                                        onClick={clearFilters}
                                    >
                                        {t('shop.catalog.reset_filters')}
                                    </button>
                                ) : null
                            }
                        />
                    )}
                </GasTabPanel>

                <GasTabPanel value="lines">
                    <div className="gas-shop-catalog-react__lines-toolbar">
                        <label className="gas-shop-catalog-react__lines-label" htmlFor="gas-shop-catalog-sort-by">
                            {t('shop.catalog.lines.sort_by')}
                        </label>
                        <select
                            id="gas-shop-catalog-sort-by"
                            className="gas-shop-catalog-react__lines-select"
                            value={sortBy}
                            onChange={onSortChange}
                        >
                            <option value="featured">{t('shop.catalog.lines.sort_featured')}</option>
                            <option value="title">{t('shop.catalog.lines.sort_title')}</option>
                            <option value="type">{t('shop.catalog.lines.sort_type')}</option>
                            <option value="price">{t('shop.catalog.lines.sort_price')}</option>
                        </select>
                        <button
                            type="button"
                            className="gas-shop-catalog-react__lines-direction"
                            onClick={toggleSortDirection}
                            aria-label={
                                sortDir === 'asc'
                                    ? t('shop.catalog.lines.direction_asc')
                                    : t('shop.catalog.lines.direction_desc')
                            }
                        >
                            {sortDir === 'asc' ? '↑' : '↓'}
                        </button>
                    </div>

                    {!showEmpty ? (
                        <div className="gas-shop-catalog-react__lines-wrap">
                            <table className="gas-shop-catalog-react__table">
                                <thead>
                                    <tr>
                                        <th>{t('shop.catalog.lines.column_title')}</th>
                                        <th>{t('shop.catalog.lines.column_type')}</th>
                                        <th>{t('shop.catalog.lines.column_slug')}</th>
                                        <th>{t('shop.catalog.lines.column_price')}</th>
                                        <th>{t('shop.catalog.lines.column_action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedItems.map(function (item) {
                                        return (
                                            <tr key={`line-${item.id}`}>
                                                <td>{item.title}</td>
                                                <td>
                                                    <span className="gas-shop-catalog-react__badges">
                                                        <GasBadge tone={typeBadgeTone(String(item.type || ''))}>
                                                            {typeLabel(String(item.type || ''))}
                                                        </GasBadge>
                                                        {item.is_featured ? (
                                                            <GasBadge tone="warning">
                                                                {t('shop.catalog.featured_badge')}
                                                            </GasBadge>
                                                        ) : null}
                                                    </span>
                                                </td>
                                                <td>
                                                    <code>{item.slug}</code>
                                                </td>
                                                <td>{formatPrice(item.price)}</td>
                                                <td>
                                                    <a className="gas-shop-catalog-react__table-link" href={item.url}>
                                                        {t('shop.catalog.lines.action_view')}
                                                    </a>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <GasEmptyState
                            title={
                                hasFilters
                                    ? t('shop.catalog.empty_filtered_title')
                                    : t('shop.catalog.empty_all_title')
                            }
                            message={
                                hasFilters
                                    ? t('shop.catalog.empty_filtered_description')
                                    : ''
                            }
                            actions={
                                hasFilters ? (
                                    <button
                                        type="button"
                                        className="gas-button gas-button--primary"
                                        onClick={clearFilters}
                                    >
                                        {t('shop.catalog.reset_filters')}
                                    </button>
                                ) : null
                            }
                        />
                    )}
                </GasTabPanel>
            </GasTabs>

            {totalPages > 1 ? (
                <nav className="gas-shop-catalog-react__pagination" aria-label={t('shop.catalog.pagination.aria')}>
                    <button
                        type="button"
                        className="gas-button gas-button--secondary gas-button--xs"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                    >
                        {t('shop.catalog.pagination.previous')}
                    </button>

                    <div className="gas-shop-catalog-react__pagination-pages">
                        {Array.from({ length: totalPages }).map(function (_item, index) {
                            const page = index + 1;
                            return (
                                <button
                                    key={page}
                                    type="button"
                                    className={[
                                        'gas-shop-catalog-react__page-button',
                                        currentPage === page ? 'is-active' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={() => goToPage(page)}
                                >
                                    {page}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        className="gas-button gas-button--secondary gas-button--xs"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                    >
                        {t('shop.catalog.pagination.next')}
                    </button>
                </nav>
            ) : null}
        </div>
    );
}
